import React, { useState, useEffect } from "react";

export interface UserInfo {
  firstName: string;
  lastName: string;
  email: string;
}

interface UserInfoFormProps {
  onSubmit: (userInfo: UserInfo) => void;
  title?: string;
  subtitle?: string;
  description?: string;
}

const UserInfoForm: React.FC<UserInfoFormProps> = ({
  onSubmit,
  title = "5-Minute Assessment",
  subtitle,
  description = "Please provide your information to get started",
}) => {
  const [formData, setFormData] = useState<UserInfo>({
    firstName: "",
    lastName: "",
    email: "",
  });
  const [errors, setErrors] = useState<Partial<UserInfo>>({});

  useEffect(() => {
    // Load user info from local storage
    const savedUserInfo = localStorage.getItem("intherax_user_info");
    if (savedUserInfo) {
      try {
        const parsedInfo = JSON.parse(savedUserInfo);
        setFormData(parsedInfo);
      } catch (error) {
        console.error("Error loading user info:", error);
      }
    }
  }, []);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field when user types
    if (errors[name as keyof UserInfo]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    const newErrors: Partial<UserInfo> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Save to local storage
    localStorage.setItem("intherax_user_info", JSON.stringify(formData));

    // Submit form
    onSubmit(formData);
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "2rem",
      }}
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "20px",
          padding: "3rem",
          maxWidth: "500px",
          width: "100%",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: "2rem" }}>
          <h1
            style={{
              fontSize: "2rem",
              fontWeight: "700",
              color: "#1a202c",
              marginBottom: "0.5rem",
            }}
          >
            {title}
          </h1>
          {subtitle && (
            <h2
              style={{
                fontSize: "1.25rem",
                fontWeight: "600",
                color: "#c9a750",
                marginBottom: "0.5rem",
              }}
            >
              {subtitle}
            </h2>
          )}
          <p
            style={{
              fontSize: "1rem",
              color: "#718096",
            }}
          >
            {description}
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: "1.5rem" }}>
            <label
              htmlFor="firstName"
              style={{
                display: "block",
                fontSize: "0.875rem",
                fontWeight: "600",
                color: "#4a5568",
                marginBottom: "0.5rem",
              }}
            >
              First Name *
            </label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              style={{
                width: "100%",
                padding: "0.75rem 1rem",
                fontSize: "1rem",
                border: errors.firstName
                  ? "2px solid #f56565"
                  : "2px solid #e2e8f0",
                borderRadius: "8px",
                outline: "none",
                transition: "border-color 0.2s",
                boxSizing: "border-box",
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = "#667eea";
              }}
              onBlur={(e) => {
                if (!errors.firstName) {
                  e.currentTarget.style.borderColor = "#e2e8f0";
                }
              }}
            />
            {errors.firstName && (
              <p
                style={{
                  color: "#f56565",
                  fontSize: "0.875rem",
                  marginTop: "0.25rem",
                }}
              >
                {errors.firstName}
              </p>
            )}
          </div>

          <div style={{ marginBottom: "1.5rem" }}>
            <label
              htmlFor="lastName"
              style={{
                display: "block",
                fontSize: "0.875rem",
                fontWeight: "600",
                color: "#4a5568",
                marginBottom: "0.5rem",
              }}
            >
              Last Name *
            </label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              style={{
                width: "100%",
                padding: "0.75rem 1rem",
                fontSize: "1rem",
                border: errors.lastName
                  ? "2px solid #f56565"
                  : "2px solid #e2e8f0",
                borderRadius: "8px",
                outline: "none",
                transition: "border-color 0.2s",
                boxSizing: "border-box",
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = "#667eea";
              }}
              onBlur={(e) => {
                if (!errors.lastName) {
                  e.currentTarget.style.borderColor = "#e2e8f0";
                }
              }}
            />
            {errors.lastName && (
              <p
                style={{
                  color: "#f56565",
                  fontSize: "0.875rem",
                  marginTop: "0.25rem",
                }}
              >
                {errors.lastName}
              </p>
            )}
          </div>

          <div style={{ marginBottom: "2rem" }}>
            <label
              htmlFor="email"
              style={{
                display: "block",
                fontSize: "0.875rem",
                fontWeight: "600",
                color: "#4a5568",
                marginBottom: "0.5rem",
              }}
            >
              Email Address *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              style={{
                width: "100%",
                padding: "0.75rem 1rem",
                fontSize: "1rem",
                border: errors.email
                  ? "2px solid #f56565"
                  : "2px solid #e2e8f0",
                borderRadius: "8px",
                outline: "none",
                transition: "border-color 0.2s",
                boxSizing: "border-box",
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = "#667eea";
              }}
              onBlur={(e) => {
                if (!errors.email) {
                  e.currentTarget.style.borderColor = "#e2e8f0";
                }
              }}
            />
            {errors.email && (
              <p
                style={{
                  color: "#f56565",
                  fontSize: "0.875rem",
                  marginTop: "0.25rem",
                }}
              >
                {errors.email}
              </p>
            )}
          </div>

          <button
            type="submit"
            style={{
              width: "100%",
              padding: "1rem",
              fontSize: "1.1rem",
              fontWeight: "600",
              color: "white",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              transition: "transform 0.2s, box-shadow 0.2s",
              boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow =
                "0 6px 20px rgba(102, 126, 234, 0.5)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow =
                "0 4px 15px rgba(102, 126, 234, 0.4)";
            }}
          >
            Start Assessment
          </button>
        </form>

        <p
          style={{
            textAlign: "center",
            fontSize: "0.75rem",
            color: "#a0aec0",
            marginTop: "1.5rem",
          }}
        >
          Your information is secure and will only be used for this assessment.
        </p>

        <p
          style={{
            textAlign: "center",
            marginTop: "0.5rem",
          }}
        >
          <a
            href="https://intheraconsultinggroup.com/"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontSize: "0.75rem",
              fontStyle: "italic",
              color: "#a0aec0",
              textDecoration: "none",
              cursor: "pointer",
            }}
          >
            Powered By{" "}
            <span
              style={{
                color: "#c9a750",
                fontStyle: "italic",
                fontSize: "0.75rem",
              }}
            >
              IntheraX
            </span>
          </a>
        </p>
      </div>
    </div>
  );
};

export default UserInfoForm;
